import { useEffect, useState } from "react";
import { useOutletContext } from "react-router-dom";
import { toast } from "sonner";
import { Plus, Trash2, Save } from "lucide-react";
import { api } from "@/lib/api";
import AccountSettings from "./AccountSettings";
import RecoveryCodes from "./RecoveryCodes";

const INPUT = "w-full px-3 py-2.5 rounded-xl border border-[#D5CCBC] bg-white text-sm text-[#1E2B1D] focus:outline-none focus:ring-2 focus:ring-[#3D5A40]";
const LABEL = "block text-xs font-bold uppercase tracking-wider text-[#354634] mb-1.5";
const EMPTY_ACCOUNT = { bank: "", number: "", holder: "" };
const DAYS = ["Senin", "Selasa", "Rabu", "Kamis", "Jumat", "Sabtu", "Minggu"];
const DEFAULT_HOURS = DAYS.map((day) => ({ day, open: "07:00", close: "21:00", closed: false }));

export default function SettingsAdmin() {
  const ctx = useOutletContext();
  const [accounts, setAccounts] = useState([EMPTY_ACCOUNT]);
  const [whatsapp, setWhatsapp] = useState("");
  const [info, setInfo] = useState({ address: "", location_note: "", maps_url: "" });
  const [hours, setHours] = useState(DEFAULT_HOURS);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    api
      .get("/settings")
      .then((r) => {
        setAccounts(r.data.bank_accounts?.length ? r.data.bank_accounts : [EMPTY_ACCOUNT]);
        setWhatsapp(r.data.whatsapp_display || "");
        setInfo({ address: r.data.address || "", location_note: r.data.location_note || "", maps_url: r.data.maps_url || "" });
        setHours(r.data.hours?.length === 7 ? r.data.hours : DEFAULT_HOURS);
      })
      .catch(() => toast.error("Gagal memuat pengaturan"))
      .finally(() => setLoading(false));
  }, []);

  const setHour = (i, k, v) => setHours((list) => list.map((h, idx) => (idx === i ? { ...h, [k]: v } : h)));
  const setInfoField = (k) => (e) => setInfo((f) => ({ ...f, [k]: e.target.value }));

  const setAccount = (i, k, v) =>
    setAccounts((list) => list.map((a, idx) => (idx === i ? { ...a, [k]: v } : a)));
  const removeAccount = (i) => setAccounts((list) => list.filter((_, idx) => idx !== i));
  const addAccount = () => setAccounts((list) => [...list, { ...EMPTY_ACCOUNT }]);

  const save = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const { data } = await api.put("/settings", { bank_accounts: accounts, whatsapp, ...info, hours });
      setAccounts(data.bank_accounts);
      setWhatsapp(data.whatsapp_display);
      setHours(data.hours);
      toast.success("Pengaturan toko tersimpan");
    } catch (err) {
      const detail = err.response?.data?.detail;
      toast.error(typeof detail === "string" ? detail : "Gagal menyimpan pengaturan");
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return <p data-testid="settings-loading" className="text-sm text-[#6B7C66]">Memuat pengaturan...</p>;
  }

  return (
    <div data-testid="settings-admin">
      <div className="mb-8">
        <h1 className="font-serif-display text-3xl font-bold text-[#1E2B1D]">Pengaturan Toko</h1>
        <p className="text-xs text-[#6B7C66] mt-1">
          Ubah nomor rekening, nomor WhatsApp, alamat toko, dan jam operasional yang tampil di web pembeli.
        </p>
      </div>

      <form onSubmit={save} className="space-y-6 max-w-2xl">
        <div className="bg-[#FBF9F5] rounded-2xl border border-[#E8E2D5] p-6">
          <div className="flex items-center justify-between mb-4">
            <span className="text-sm font-bold text-[#1E2B1D]">Rekening Bank</span>
            <button
              type="button"
              onClick={addAccount}
              data-testid="settings-add-account-btn"
              className="inline-flex items-center gap-1.5 text-xs font-semibold text-[#3D5A40] hover:underline"
            >
              <Plus className="w-3.5 h-3.5" /> Tambah Rekening
            </button>
          </div>
          <div className="space-y-4">
            {accounts.map((a, i) => (
              <div
                key={i}
                data-testid={`settings-account-${i + 1}`}
                className="grid grid-cols-1 sm:grid-cols-[1.2fr_1fr_1fr_auto] gap-3 items-end p-4 rounded-xl bg-[#F4EFE6] border border-[#E0D7C6]"
              >
                <div>
                  <label className={LABEL}>Nama Bank</label>
                  <input
                    value={a.bank}
                    onChange={(e) => setAccount(i, "bank", e.target.value)}
                    data-testid={`settings-bank-name-${i + 1}`}
                    placeholder="Bank Central Asia (BCA)"
                    className={INPUT}
                  />
                </div>
                <div>
                  <label className={LABEL}>Nomor Rekening</label>
                  <input
                    value={a.number}
                    onChange={(e) => setAccount(i, "number", e.target.value)}
                    data-testid={`settings-bank-number-${i + 1}`}
                    placeholder="0661443161"
                    className={`${INPUT} font-mono`}
                  />
                </div>
                <div>
                  <label className={LABEL}>Atas Nama</label>
                  <input
                    value={a.holder}
                    onChange={(e) => setAccount(i, "holder", e.target.value)}
                    data-testid={`settings-bank-holder-${i + 1}`}
                    placeholder="Maya Puspita S"
                    className={INPUT}
                  />
                </div>
                <button
                  type="button"
                  onClick={() => removeAccount(i)}
                  disabled={accounts.length === 1}
                  data-testid={`settings-remove-account-${i + 1}`}
                  aria-label="Hapus rekening"
                  className="p-2.5 rounded-xl border border-[#D5CCBC] text-red-600 hover:bg-red-50 disabled:opacity-30 transition-colors"
                >
                  <Trash2 className="w-4 h-4" />
                </button>
              </div>
            ))}
          </div>
        </div>

        <div className="bg-[#FBF9F5] rounded-2xl border border-[#E8E2D5] p-6">
          <label className={LABEL}>Nomor WhatsApp Toko</label>
          <input
            value={whatsapp}
            onChange={(e) => setWhatsapp(e.target.value)}
            data-testid="settings-whatsapp-input"
            placeholder="0813-2497-7359"
            className={`${INPUT} font-mono max-w-xs`}
          />
          <p className="text-[11px] text-[#7E8F7A] mt-2">
            Dipakai untuk tombol konfirmasi & cek status pesanan pembeli. Format 08xx otomatis diubah ke 62xx.
          </p>
        </div>

        <div className="bg-[#FBF9F5] rounded-2xl border border-[#E8E2D5] p-6 space-y-4">
          <span className="text-sm font-bold text-[#1E2B1D] block">Alamat Toko</span>
          <div>
            <label className={LABEL}>Alamat Lengkap</label>
            <textarea
              value={info.address}
              onChange={setInfoField("address")}
              data-testid="settings-address-input"
              rows="2"
              placeholder="RT.001/RW.012, Harapan Baru, Bekasi Utara, Kota Bekasi, Jawa Barat 17123"
              className={INPUT}
            />
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <div>
              <label className={LABEL}>Keterangan Lokasi</label>
              <input
                value={info.location_note}
                onChange={setInfoField("location_note")}
                data-testid="settings-location-note-input"
                placeholder="Berlokasi di Prima Orchard Trade Mall"
                className={INPUT}
              />
            </div>
            <div>
              <label className={LABEL}>Link Google Maps</label>
              <input
                value={info.maps_url}
                onChange={setInfoField("maps_url")}
                data-testid="settings-maps-url-input"
                placeholder="https://maps.google.com/..."
                className={INPUT}
              />
            </div>
          </div>
        </div>

        <div className="bg-[#FBF9F5] rounded-2xl border border-[#E8E2D5] p-6">
          <span className="text-sm font-bold text-[#1E2B1D] block mb-4">Jam Operasional</span>
          <div className="space-y-2">
            {hours.map((h, i) => (
              <div
                key={h.day}
                data-testid={`settings-hours-${h.day.toLowerCase()}`}
                className="grid grid-cols-[5rem_1fr_auto_1fr_auto] items-center gap-2 text-sm"
              >
                <span className="font-semibold text-[#354634]">{h.day}</span>
                <input
                  type="time"
                  value={h.open}
                  disabled={h.closed}
                  onChange={(e) => setHour(i, "open", e.target.value)}
                  data-testid={`settings-hours-open-${h.day.toLowerCase()}`}
                  className={`${INPUT} py-1.5 disabled:opacity-40`}
                />
                <span className="text-[#7E8F7A]">–</span>
                <input
                  type="time"
                  value={h.close}
                  disabled={h.closed}
                  onChange={(e) => setHour(i, "close", e.target.value)}
                  data-testid={`settings-hours-close-${h.day.toLowerCase()}`}
                  className={`${INPUT} py-1.5 disabled:opacity-40`}
                />
                <label className="flex items-center gap-1.5 text-xs text-[#5E6D5B] whitespace-nowrap">
                  <input
                    type="checkbox"
                    checked={h.closed}
                    onChange={(e) => setHour(i, "closed", e.target.checked)}
                    data-testid={`settings-hours-closed-${h.day.toLowerCase()}`}
                    className="accent-[#3D5A40]"
                  />
                  Tutup
                </label>
              </div>
            ))}
          </div>
        </div>

        <button
          type="submit"
          disabled={saving}
          data-testid="settings-save-btn"
          className="inline-flex items-center gap-2 px-6 py-3 rounded-xl bg-[#3D5A40] hover:bg-[#2E4530] disabled:opacity-50 text-[#FAF7EE] font-bold text-sm transition-colors shadow-md"
        >
          <Save className="w-4 h-4" />
          {saving ? "Menyimpan..." : "Simpan Pengaturan"}
        </button>
      </form>

      <div className="max-w-2xl mt-10 pt-8 border-t border-[#E0D7C6] space-y-6">
        <AccountSettings user={ctx?.user} onUpdated={(u) => ctx?.setUser?.((prev) => ({ ...prev, ...u }))} />
        <RecoveryCodes />
      </div>
    </div>
  );
}
